import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  serverTimestamp,
  FieldValue
} from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { toast } from 'react-hot-toast';
import { updateInventoryDatabaseItem } from './inventoryService';
import {
  JEWELRY_CATEGORIES,
  suggestCategoryByProductType,
  normalizeCategoryGid,
  normalizeProductTypeForShopify,
} from '../constants/shopifyCategories';

// Product Entry Session Types
export interface ProductEntrySession {
  id: string;
  sessionId: string; // Unique ID for the entire product entry process (based on SKU)
  sku: string; // The SKU this session is for
  category: 'jewelry' | 'watches';
  status: 'draft' | 'in_progress' | 'completed' | 'cancelled';
  currentStep: number;
  totalSteps: number;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  createdByName: string;
  completedAt?: Date;
  
  // Audit/History tracking (invisible to users)
  audit: {
    totalTimeSpent: number; // in milliseconds
    lastActivityAt: Date;
    activityCount: number;
    sessions: ActivitySession[];
    changes: ChangeLog[];
    metadata: SessionMetadata;
  };
}

// Activity tracking for each user session
export interface ActivitySession {
  id: string;
  userId: string;
  userName: string;
  startTime: Date;
  endTime?: Date;
  duration?: number; // in milliseconds
  actions: UserAction[];
  ipAddress?: string;
  userAgent?: string;
}

// Individual user actions
export interface UserAction {
  id: string;
  timestamp: Date;
  action: 'view' | 'edit' | 'save' | 'delete' | 'navigate' | 'upload' | 'download' | 'print' | 'export';
  step: number;
  field?: string; // which field was modified
  oldValue?: any; // previous value
  newValue?: any; // new value
  description: string; // human readable description
  metadata?: Record<string, any>; // additional context
}

// Change log for tracking modifications
export interface ChangeLog {
  id: string;
  timestamp: Date;
  userId: string;
  userName: string;
  changeType: 'create' | 'update' | 'delete' | 'status_change' | 'step_change';
  field?: string;
  oldValue?: any;
  newValue?: any;
  reason?: string;
  ipAddress?: string;
  userAgent?: string;
}

// Session metadata
export interface SessionMetadata {
  deviceInfo: {
    platform: string;
    browser: string;
    version: string;
    screenResolution?: string;
    timezone: string;
  };
  performance: {
    loadTime: number;
    renderTime: number;
    apiCalls: number;
    errors: number;
  };
  navigation: {
    pageViews: number;
    uniquePages: string[];
    timeOnPage: Record<string, number>;
  };
  collaboration: {
    concurrentUsers: string[];
    lastCollaborationAt?: Date;
    sharedWith: string[];
  };
}

// Edit History Entry
export interface EditHistoryEntry {
  id: string;
  timestamp: Date;
  editedBy: string;
  editedByName: string;
  phase: 'review' | 'corrections' | 'final_review' | 'post_approval';
  changes: Array<{
    field: string;
    fieldLabel: string;
    oldValue: any;
    newValue: any;
    changeType: 'added' | 'modified' | 'removed';
  }>;
  note?: string;
}

// Jewelry Product Entry Types
export interface JewelryProductEntry {
  id: string;
  sessionId: string;
  category: 'jewelry';
  
  // Basic Information
  title: string;
  sku: string;
  price: string; // Shopify uses string for price
  
  // Full product data (everything from the form)
  productData: any;
  
  // Status and Tracking
  status: 'draft' | 'in_progress' | 'pending_review' | 'corrections_needed' | 'final_review' | 'partially_approved' | 'approved' | 'rejected';
  submittedBy: string;
  submittedByName: string;
  submittedAt?: Date;
  reviewedBy?: string;
  reviewedByName?: string;
  reviewedAt?: Date;
  reviewNotes?: string;
  reviewChecklist?: Record<string, { passed: boolean | null; notes: string }>;
  
  // Initial Review Tracking (captures first review accuracy)
  initialReviewChecklist?: Record<string, { passed: boolean | null; notes: string }>;
  initialReviewAccuracyPercentage?: number;
  correctionsRequestedAt?: Date;
  finalReviewSubmittedAt?: Date;
  
  /** Created by a Dual Role user: needs a single approval instead of two. */
  dualRoleEntry?: boolean;

  // Template Completion Time Tracking
  sessionStartedAt?: Date;  // When the product entry session was started
  firstSavedAt?: Date;      // When the product was first saved (draft)
  firstSubmittedAt?: Date;  // When the product was first submitted for review
  templateCompletionTime?: number;  // Time in milliseconds from session start to first save/submit
  
  // Edit History
  editHistory?: EditHistoryEntry[];
  
  // Shopify Integration
  shopifyProductId?: string;
  pushedToShopifyAt?: Date;
  // Who initiated the Shopify push (for per-employee "Published to CCO" reporting).
  pushedToShopifyByUid?: string;
  pushedToShopifyByName?: string;
  
  // Async Shopify Push Status
  shopifyPushStatus?: 'idle' | 'pushing' | 'success' | 'error' | 'partial_success';
  shopifyPushError?: string;
  shopifyPushStartedAt?: Date;
  shopifyPushCompletedAt?: Date;
  shopifyPushLogs?: Array<{
    timestamp: Date;
    level: 'info' | 'warning' | 'error' | 'success';
    category: 'product' | 'images' | 'metafields' | 'sales_channels' | 'inventory';
    message: string;
    details?: any;
  }>;
  shopifyPushErrorAcknowledged?: boolean;
  shopifyPushErrorAcknowledgedBy?: string;
  shopifyPushErrorAcknowledgedAt?: Date;
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  createdByName: string;
}

// Watches Product Entry Types
export interface WatchesProductEntry {
  id: string;
  sessionId: string;
  category: 'watches';
  
  // Basic Information
  name: string;
  description: string;
  sku: string;
  price: number;
  cost?: number;
  
  // Watch Specific Fields
  brand: string;
  model: string;
  movement: string;
  caseMaterial: string;
  bandMaterial: string;
  bandColor: string;
  dialColor: string;
  caseSize: string;
  waterResistance?: string;
  features: string[];
  
  // Images and Media
  images: string[];
  videos: string[];
  
  // Status and Tracking
  status: 'draft' | 'pending_review' | 'approved' | 'rejected';
  notes?: string;
  tags: string[];
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  createdByName: string;
}

// Form Step Configuration
export interface FormStep {
  stepNumber: number;
  stepName: string;
  isCompleted: boolean;
  isRequired: boolean;
  fields: string[];
}

// Product Entry Session Configuration
export const PRODUCT_ENTRY_CONFIG = {
  jewelry: {
    totalSteps: 5,
    steps: [
      { stepNumber: 1, stepName: 'Basic Information', isCompleted: false, isRequired: true, fields: ['name', 'description', 'sku', 'price'] },
      { stepNumber: 2, stepName: 'Jewelry Details', isCompleted: false, isRequired: true, fields: ['metalType', 'gemstoneType', 'caratWeight'] },
      { stepNumber: 3, stepName: 'Specifications', isCompleted: false, isRequired: false, fields: ['ringSize', 'chainLength', 'setting', 'finish'] },
      { stepNumber: 4, stepName: 'Media & Images', isCompleted: false, isRequired: true, fields: ['images', 'videos'] },
      { stepNumber: 5, stepName: 'Review & Submit', isCompleted: false, isRequired: true, fields: ['notes', 'tags'] }
    ]
  },
  watches: {
    totalSteps: 5,
    steps: [
      { stepNumber: 1, stepName: 'Basic Information', isCompleted: false, isRequired: true, fields: ['name', 'description', 'sku', 'price'] },
      { stepNumber: 2, stepName: 'Watch Details', isCompleted: false, isRequired: true, fields: ['brand', 'model', 'movement'] },
      { stepNumber: 3, stepName: 'Specifications', isCompleted: false, isRequired: false, fields: ['caseMaterial', 'bandMaterial', 'caseSize', 'waterResistance'] },
      { stepNumber: 4, stepName: 'Media & Images', isCompleted: false, isRequired: true, fields: ['images', 'videos'] },
      { stepNumber: 5, stepName: 'Review & Submit', isCompleted: false, isRequired: true, fields: ['notes', 'tags'] }
    ]
  }
};

const REVIEW_FEED_KEEP_MS = 10 * 60_000;
const reviewFeed = {
  unsub: null as null | (() => void),
  products: null as JewelryProductEntry[] | null,
  byId: new Map<string, JewelryProductEntry>(),
  listeners: new Set<(products: JewelryProductEntry[]) => void>(),
  stopTimer: undefined as ReturnType<typeof setTimeout> | undefined,
};

class ProductEntryService {
  private userId: string = '';
  private userName: string = '';

  setUserId(userId: string, userName?: string) {
    this.userId = userId;
    this.userName = userName || '';
  }

  // Generate unique session ID based on SKU
  generateSessionId(sku: string): string {
    // Clean SKU: remove spaces, special chars, convert to lowercase
    const cleanSku = sku.trim().replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    const timestamp = Date.now();
    return `sku_${cleanSku}_${timestamp}`;
  }

  // Alternative: Generate session ID from SKU only (if SKU is unique enough)
  generateSessionIdFromSku(sku: string): string {
    const cleanSku = sku.trim().replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    return `sku_${cleanSku}`;
  }

  // Generate unique IDs for audit tracking
  generateActionId(): string {
    return `action_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  }

  generateChangeId(): string {
    return `change_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  }

  // Helper function to create a human-readable field label
  private formatFieldLabel(field: string): string {
    // Convert camelCase or snake_case to readable text
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase())
      .trim();
  }

  // Helper function to detect changes between old and new product data
  private detectProductChanges(oldData: any, newData: any): Array<{
    field: string;
    fieldLabel: string;
    oldValue: any;
    newValue: any;
    changeType: 'added' | 'modified' | 'removed';
  }> {
    const changes: Array<{
      field: string;
      fieldLabel: string;
      oldValue: any;
      newValue: any;
      changeType: 'added' | 'modified' | 'removed';
    }> = [];

    if (!oldData || !newData) return changes;

    // Helper to compare values
    const isDifferent = (val1: any, val2: any): boolean => {
      // Handle arrays
      if (Array.isArray(val1) && Array.isArray(val2)) {
        return JSON.stringify(val1) !== JSON.stringify(val2);
      }
      // Handle objects (except arrays)
      if (typeof val1 === 'object' && val1 !== null && typeof val2 === 'object' && val2 !== null) {
        return JSON.stringify(val1) !== JSON.stringify(val2);
      }
      return val1 !== val2;
    };

    // Helper to format value for display
    const formatValue = (val: any): any => {
      if (val === null || val === undefined) return 'N/A';
      if (Array.isArray(val)) {
        if (val.length === 0) return 'None';
        // For image/video URLs, just show count
        if (val.length > 0 && typeof val[0] === 'string' && (val[0].includes('http') || val[0].includes('firebasestorage'))) {
          return `${val.length} file(s)`;
        }
        return val.join(', ');
      }
      if (typeof val === 'object') {
        // Don't show full objects in history
        return '[Object]';
      }
      return val;
    };

    // Check main product fields
    const fieldsToCheck = ['title', 'price', 'body_html', 'vendor', 'product_type', 'tags', 'status'];
    fieldsToCheck.forEach(field => {
      const oldVal = oldData[field];
      const newVal = newData[field];
      
      if (isDifferent(oldVal, newVal)) {
        changes.push({
          field,
          fieldLabel: this.formatFieldLabel(field),
          oldValue: formatValue(oldVal),
          newValue: formatValue(newVal),
          changeType: !oldVal ? 'added' : !newVal ? 'removed' : 'modified'
        });
      }
    });

    // Check variant fields (first variant)
    if (oldData.variants?.[0] && newData.variants?.[0]) {
      const variantFields = ['price', 'compare_at_price', 'sku', 'barcode', 'weight', 'inventory_quantity'];
      variantFields.forEach(field => {
        const oldVal = oldData.variants[0][field];
        const newVal = newData.variants[0][field];
        
        if (isDifferent(oldVal, newVal)) {
          changes.push({
            field: `variant.${field}`,
            fieldLabel: `Variant ${this.formatFieldLabel(field)}`,
            oldValue: formatValue(oldVal),
            newValue: formatValue(newVal),
            changeType: !oldVal ? 'added' : !newVal ? 'removed' : 'modified'
          });
        }
      });
    }

    // Check metafields
    if (oldData.metafields || newData.metafields) {
      const oldMetafields = oldData.metafields || [];
      const newMetafields = newData.metafields || [];
      
      // Compare metafields by namespace.key
      const metafieldMap = new Map();
      oldMetafields.forEach((mf: any) => {
        metafieldMap.set(`${mf.namespace}.${mf.key}`, { old: mf.value });
      });
      newMetafields.forEach((mf: any) => {
        const key = `${mf.namespace}.${mf.key}`;
        if (metafieldMap.has(key)) {
          metafieldMap.get(key).new = mf.value;
        } else {
          metafieldMap.set(key, { new: mf.value });
        }
      });

      metafieldMap.forEach((values, key) => {
        if (values.old !== values.new) {
          changes.push({
            field: `metafield.${key}`,
            fieldLabel: `Metafield: ${key}`,
            oldValue: formatValue(values.old),
            newValue: formatValue(values.new),
            changeType: !values.old ? 'added' : !values.new ? 'removed' : 'modified'
          });
        }
      });
    }

    // Check product details
    if (oldData.productDetails || newData.productDetails) {
      const oldDetails = oldData.productDetails || {};
      const newDetails = newData.productDetails || {};
      
      const allKeys = new Set([...Object.keys(oldDetails), ...Object.keys(newDetails)]);
      allKeys.forEach(key => {
        const oldVal = oldDetails[key];
        const newVal = newDetails[key];
        
        if (isDifferent(oldVal, newVal)) {
          changes.push({
            field: `productDetails.${key}`,
            fieldLabel: this.formatFieldLabel(key),
            oldValue: formatValue(oldVal),
            newValue: formatValue(newVal),
            changeType: !oldVal ? 'added' : !newVal ? 'removed' : 'modified'
          });
        }
      });
    }

    // Check images
    const oldImages = oldData.imageUrls || oldData.images || [];
    const newImages = newData.imageUrls || newData.images || [];
    if (oldImages.length !== newImages.length) {
      changes.push({
        field: 'images',
        fieldLabel: 'Images',
        oldValue: `${oldImages.length} image(s)`,
        newValue: `${newImages.length} image(s)`,
        changeType: oldImages.length < newImages.length ? 'added' : 'removed'
      });
    }

    return changes;
  }

  generateActivitySessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  }

  // Get device and browser information
  private getDeviceInfo() {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const screenResolution = `${screen.width}x${screen.height}`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Simple browser detection
    let browser = 'Unknown';
    let version = 'Unknown';
    
    if (userAgent.includes('Chrome')) {
      browser = 'Chrome';
      const match = userAgent.match(/Chrome\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Firefox')) {
      browser = 'Firefox';
      const match = userAgent.match(/Firefox\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Safari')) {
      browser = 'Safari';
      const match = userAgent.match(/Version\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    }

    return {
      platform,
      browser,
      version,
      screenResolution,
      timezone
    };
  }

  // Track user action
  async trackUserAction(
    sessionId: string, 
    action: UserAction['action'], 
    step: number, 
    field?: string, 
    oldValue?: any, 
    newValue?: any, 
    description?: string
  ): Promise<void> {
    try {
      const session = await this.getProductEntrySession(sessionId);
      if (!session) return;

      const actionId = this.generateActionId();
      const now = new Date();
      
      const userAction: UserAction = {
        id: actionId,
        timestamp: now,
        action,
        step,
        field,
        oldValue,
        newValue,
        description: description || `${action} on ${field || 'session'} at step ${step}`,
        metadata: {
          userAgent: navigator.userAgent,
          url: window.location.href
        }
      };

      // Update session with new action
      const updatedSession = {
        ...session,
        audit: {
          ...session.audit,
          lastActivityAt: now,
          activityCount: session.audit.activityCount + 1,
          sessions: session.audit.sessions.map(s => 
            s.id === this.getCurrentActivitySessionId(sessionId) 
              ? { ...s, actions: [...s.actions, userAction] }
              : s
          )
        }
      };

      await this.updateProductEntrySession(sessionId, updatedSession);
    } catch (error) {
      console.error('Error tracking user action:', error);
    }
  }

  // Track changes to session data
  async trackChange(
    sessionId: string,
    changeType: ChangeLog['changeType'],
    field?: string,
    oldValue?: any,
    newValue?: any,
    reason?: string
  ): Promise<void> {
    try {
      const session = await this.getProductEntrySession(sessionId);
      if (!session) return;

      const changeId = this.generateChangeId();
      const now = new Date();
      
      const change: ChangeLog = {
        id: changeId,
        timestamp: now,
        userId: this.userId,
        userName: this.userName,
        changeType,
        field,
        oldValue,
        newValue,
        reason,
        ipAddress: await this.getClientIP(),
        userAgent: navigator.userAgent
      };

      // Update session with new change
      const updatedSession = {
        ...session,
        audit: {
          ...session.audit,
          changes: [...session.audit.changes, change]
        }
      };

      await this.updateProductEntrySession(sessionId, updatedSession);
    } catch (error) {
      console.error('Error tracking change:', error);
    }
  }

  // Start activity session
  async startActivitySession(sessionId: string): Promise<string> {
    try {
      const session = await this.getProductEntrySession(sessionId);
      if (!session) return '';

      const activitySessionId = this.generateActivitySessionId();
      const now = new Date();
      
      const activitySession: ActivitySession = {
        id: activitySessionId,
        userId: this.userId,
        userName: this.userName,
        startTime: now,
        actions: [],
        ipAddress: await this.getClientIP(),
        userAgent: navigator.userAgent
      };

      // Update session with new activity session
      const updatedSession = {
        ...session,
        audit: {
          ...session.audit,
          sessions: [...session.audit.sessions, activitySession]
        }
      };

      await this.updateProductEntrySession(sessionId, updatedSession);
      return activitySessionId;
    } catch (error) {
      console.error('Error starting activity session:', error);
      return '';
    }
  }

  // End activity session
  async endActivitySession(sessionId: string, activitySessionId: string): Promise<void> {
    try {
      const session = await this.getProductEntrySession(sessionId);
      if (!session) return;

      const now = new Date();
      const activitySession = session.audit.sessions.find(s => s.id === activitySessionId);
      if (!activitySession) return;

      const duration = now.getTime() - activitySession.startTime.getTime();
      
      const updatedActivitySession = {
        ...activitySession,
        endTime: now,
        duration
      };

      // Update session with ended activity session
      const updatedSession = {
        ...session,
        audit: {
          ...session.audit,
          totalTimeSpent: session.audit.totalTimeSpent + duration,
          sessions: session.audit.sessions.map(s => 
            s.id === activitySessionId ? updatedActivitySession : s
          )
        }
      };

      await this.updateProductEntrySession(sessionId, updatedSession);
    } catch (error) {
      console.error('Error ending activity session:', error);
    }
  }

  // Get current activity session ID (for tracking)
  private getCurrentActivitySessionId(sessionId: string): string {
    // This would be stored in memory or localStorage for the current user session
    return localStorage.getItem(`activity_session_${sessionId}`) || '';
  }

  // Get client IP (simplified - in production you'd use a service)
  private async getClientIP(): Promise<string> {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch {
      return 'unknown';
    }
  }

  // Create new product entry session
  async createProductEntrySession(category: 'jewelry' | 'watches', sku: string, createdByName: string): Promise<ProductEntrySession> {
    try {
      const sessionId = this.generateSessionId(sku);
      const now = new Date();
      const deviceInfo = this.getDeviceInfo();
      
      const sessionData: Omit<ProductEntrySession, 'id'> = {
        sessionId,
        sku,
        category,
        status: 'draft',
        currentStep: 1,
        totalSteps: PRODUCT_ENTRY_CONFIG[category].totalSteps,
        createdAt: now,
        updatedAt: now,
        createdBy: this.userId,
        createdByName,
        audit: {
          totalTimeSpent: 0,
          lastActivityAt: now,
          activityCount: 0,
          sessions: [],
          changes: [],
          metadata: {
            deviceInfo,
            performance: {
              loadTime: 0,
              renderTime: 0,
              apiCalls: 0,
              errors: 0
            },
            navigation: {
              pageViews: 0,
              uniquePages: [],
              timeOnPage: {}
            },
            collaboration: {
              concurrentUsers: [this.userId],
              sharedWith: []
            }
          }
        }
      };

      const docRef = await addDoc(collection(db, 'productEntrySessions'), {
        ...sessionData,
        createdAt: Timestamp.fromDate(sessionData.createdAt),
        updatedAt: Timestamp.fromDate(sessionData.updatedAt),
        audit: {
          ...sessionData.audit,
          lastActivityAt: Timestamp.fromDate(sessionData.audit.lastActivityAt)
        }
      });

      // Return the full session object with the doc ID
      return {
        ...sessionData,
        id: docRef.id
      };
    } catch (error) {
      console.error('Error creating product entry session:', error);
      throw error;
    }
  }

  // Get product entry session
  async getProductEntrySession(sessionId: string): Promise<ProductEntrySession | null> {
    try {
      const q = query(
        collection(db, 'productEntrySessions'),
        where('sessionId', '==', sessionId)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          completedAt: data.completedAt?.toDate()
        } as ProductEntrySession;
      }
      return null;
    } catch (error) {
      console.error('Error getting product entry session:', error);
      throw error;
    }
  }

  // Update product entry session
  async updateProductEntrySession(sessionId: string, updates: Partial<ProductEntrySession>): Promise<void> {
    try {
      const q = query(
        collection(db, 'productEntrySessions'),
        where('sessionId', '==', sessionId)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        await updateDoc(docRef, {
          ...updates,
          updatedAt: Timestamp.now()
        });
      }
    } catch (error) {
      console.error('Error updating product entry session:', error);
      throw error;
    }
  }

  // Save jewelry product entry
  async saveJewelryProductEntry(
    sessionId: string,
    productData: any,
    userName: string,
    existingProductId?: string,
    options?: { dualRoleEntry?: boolean },
  ): Promise<string> {
    try {
      const now = new Date();
      
      // productData now contains imageUrls and videoUrls (strings) which are safe for Firestore
      // No need to clean anything as we're no longer storing File objects
      const cleanProductData = {
        ...productData
      };

      // If we have an existing product ID (editing mode), update that specific product
      if (existingProductId) {
        console.log('Updating existing product:', existingProductId);
        
        // Get the existing product to detect changes
        const existingProductDoc = await getDoc(doc(db, 'jewelryProductEntries', existingProductId));
        if (!existingProductDoc.exists()) {
          throw new Error('Product not found');
        }
        
        const existingProduct = existingProductDoc.data() as JewelryProductEntry;
        const oldProductData = existingProduct.productData || {};
        
        // Detect changes
        const changes = this.detectProductChanges(oldProductData, cleanProductData);
        
        // Determine the phase based on product status
        let phase: 'review' | 'corrections' | 'final_review' | 'post_approval' = 'review';
        if (existingProduct.status === 'corrections_needed') {
          phase = 'corrections';
        } else if (existingProduct.status === 'final_review') {
          phase = 'final_review';
        } else if (existingProduct.status === 'approved' || existingProduct.status === 'partially_approved') {
          phase = 'post_approval';
        }
        
        // Create edit history entry if there are changes
        const updateData: any = {
          title: productData.title || '',
          sku: productData.variants?.[0]?.sku || '',
          price: productData.variants?.[0]?.price || '0',
          productData: cleanProductData,
          updatedAt: Timestamp.fromDate(now)
        };

        // A draft Claire started for photos is "templated" when a person first fills in its information.
        const existingAny = existingProduct as any;
        if (existingAny.createdByClaire && !existingAny.templatedAt &&
            changes.some((c) => !/^(imageUrls|images|videoUrls)\b/.test(c.field))) {
          updateData.templatedAt = Timestamp.fromDate(now);
          updateData.templatedBy = this.userId;
          updateData.templatedByName = userName;
        }
        
        if (changes.length > 0) {
          const editHistoryEntry: EditHistoryEntry = {
            id: this.generateChangeId(),
            timestamp: now,
            editedBy: this.userId,
            editedByName: userName,
            phase,
            changes
          };
          
          // Append to existing edit history
          const existingHistory = existingProduct.editHistory || [];
          updateData.editHistory = [
            ...existingHistory.map(entry => ({
              ...entry,
              timestamp: entry.timestamp instanceof Date ? Timestamp.fromDate(entry.timestamp) : entry.timestamp
            })),
            {
              ...editHistoryEntry,
              timestamp: Timestamp.fromDate(editHistoryEntry.timestamp)
            }
          ];
          
          console.log('Edit history recorded:', changes.length, 'changes detected');
        }
        
        await updateDoc(doc(db, 'jewelryProductEntries', existingProductId), updateData);
        
        await this.syncInventoryFromProductData(cleanProductData);
        return existingProductId;
      }

      // Check if a product already exists for this session
      const q = query(
        collection(db, 'jewelryProductEntries'),
        where('sessionId', '==', sessionId)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Update existing product
        const existingDoc = querySnapshot.docs[0];
        const existingData = existingDoc.data();
        const oldProductData = existingData.productData || {};
        
        // Detect changes
        const changes = this.detectProductChanges(oldProductData, cleanProductData);
        
        // Determine the phase based on product status
        let phase: 'review' | 'corrections' | 'final_review' | 'post_approval' = 'review';
        if (existingData.status === 'corrections_needed') {
          phase = 'corrections';
        } else if (existingData.status === 'final_review') {
          phase = 'final_review';
        } else if (existingData.status === 'approved' || existingData.status === 'partially_approved') {
          phase = 'post_approval';
        }
        
        console.log('Updating product found by sessionId:', existingDoc.id);
        
        const updateData: any = {
          title: productData.title || '',
          sku: productData.variants?.[0]?.sku || '',
          price: productData.variants?.[0]?.price || '0',
          productData: cleanProductData,
          updatedAt: Timestamp.fromDate(now)
        };
        
        if (changes.length > 0) {
          const editHistoryEntry: EditHistoryEntry = {
            id: this.generateChangeId(),
            timestamp: now,
            editedBy: this.userId,
            editedByName: userName,
            phase,
            changes
          };
          
          // Append to existing edit history
          const existingHistory = existingData.editHistory || [];
          updateData.editHistory = [
            ...existingHistory.map((entry: any) => ({
              ...entry,
              timestamp: entry.timestamp instanceof Date ? Timestamp.fromDate(entry.timestamp) : entry.timestamp
            })),
            {
              ...editHistoryEntry,
              timestamp: Timestamp.fromDate(editHistoryEntry.timestamp)
            }
          ];
          
          console.log('Edit history recorded:', changes.length, 'changes detected');
        }
        
        await updateDoc(doc(db, 'jewelryProductEntries', existingDoc.id), updateData);
        
        await this.syncInventoryFromProductData(cleanProductData);
        return existingDoc.id;
      } else {
        // Create new product
        console.log('Creating new product for session:', sessionId);
        
        // Fetch the session to get the session start time
        const session = await this.getProductEntrySession(sessionId);
        const sessionStartedAt = session?.createdAt || now;
        const firstSavedAt = now;
        
        // Calculate template completion time (time from session start to first save)
        const templateCompletionTime = firstSavedAt.getTime() - (sessionStartedAt instanceof Date ? sessionStartedAt.getTime() : new Date(sessionStartedAt).getTime());
        
        console.log('⏱️ Template completion time tracking:', {
          sessionStartedAt,
          firstSavedAt,
          templateCompletionTime: `${Math.round(templateCompletionTime / 1000)}s (${templateCompletionTime}ms)`
        });
        
        // Detect category from productData (watches store details under `watchDetails`)
        const isWatchEntry = !!cleanProductData?.watchDetails || cleanProductData?.product_type === 'Watches';
        const detectedCategory: 'jewelry' | 'watches' = isWatchEntry ? 'watches' : 'jewelry';

        const entry: Omit<JewelryProductEntry, 'id'> = {
          sessionId,
          category: detectedCategory as 'jewelry',
          title: productData.title || '',
          sku: productData.variants?.[0]?.sku || '',
          price: productData.variants?.[0]?.price || '0',
          productData: cleanProductData,
          status: 'draft',
          submittedBy: this.userId,
          submittedByName: userName,
          createdAt: now,
          updatedAt: now,
          createdBy: this.userId,
          createdByName: userName,
          editHistory: [], // Initialize empty edit history
          // Template completion time tracking
          sessionStartedAt,
          firstSavedAt,
          templateCompletionTime
        };

        const docRef = await addDoc(collection(db, 'jewelryProductEntries'), {
          ...entry,
          ...(options?.dualRoleEntry ? { dualRoleEntry: true } : {}),
          createdAt: Timestamp.fromDate(entry.createdAt),
          updatedAt: Timestamp.fromDate(entry.updatedAt),
          sessionStartedAt: Timestamp.fromDate(sessionStartedAt instanceof Date ? sessionStartedAt : new Date(sessionStartedAt)),
          firstSavedAt: Timestamp.fromDate(entry.firstSavedAt as Date)
        });

        // Sync inventory status to 'draft' when product is first created
        try {
          const sku = entry.sku;
          if (sku) {
            console.log(`[ProductEntry] New product created, syncing inventory for SKU: ${sku}`);
            await this.syncInventoryWorkflowStatus(sku, 'draft');
          }
        } catch (syncError) {
          console.error('Error syncing inventory status on create:', syncError);
        }

        await this.syncInventoryFromProductData(cleanProductData);
        return docRef.id;
      }
    } catch (error) {
      console.error('Error saving jewelry product entry:', error);
      throw error;
    }
  }

  // Submit jewelry product for review
  async submitJewelryProductForReview(productId: string, userName: string): Promise<void> {
    try {
      const productRef = doc(db, 'jewelryProductEntries', productId);
      
      // Get the jewelry product to find its sessionId
      const productSnap = await getDoc(productRef);
      const productData = productSnap.data();
      
      // Try to get audit data from the session
      let auditData: any = null;
      if (productData?.sessionId) {
        const session = await this.getProductEntrySession(productData.sessionId);
        if (session?.audit) {
          auditData = {
            totalTimeSpent: session.audit.totalTimeSpent,
            lastActivityAt: session.audit.lastActivityAt,
            activityCount: session.audit.activityCount
          };
          console.log('⏱️ Copying audit data to product entry:', auditData.totalTimeSpent, 'ms');
        }
      }
      
      // Use serverTimestamp() for accurate timestamps (avoids client clock issues)
      const updateData: any = {
        status: 'pending_review',
        submittedAt: serverTimestamp(),
        submittedBy: this.userId,
        submittedByName: userName,
        updatedAt: serverTimestamp()
      };
      
      // Include audit data if available
      if (auditData) {
        updateData.audit = auditData;
      }
      
      await updateDoc(productRef, updateData);
    } catch (error) {
      console.error('Error submitting product for review:', error);
      throw error;
    }
  }

  // Update product status
  async updateProductStatus(
    productId: string, 
    status: 'draft' | 'in_progress' | 'pending_review' | 'corrections_needed' | 'final_review' | 'partially_approved' | 'approved' | 'rejected',
    userName: string
  ): Promise<void> {
    try {
      const productRef = doc(db, 'jewelryProductEntries', productId);
      
      // First, get the current product to check if firstSubmittedAt exists
      const productSnap = await getDoc(productRef);
      const currentData = productSnap.exists() ? productSnap.data() : null;
      
      // Use serverTimestamp() for accurate timestamps (avoids client clock issues)
      const updateData: any = {
        status,
        submittedBy: this.userId,
        submittedByName: userName,
        updatedAt: serverTimestamp()
      };
      
      // Only set submittedAt if it doesn't already exist (preserve original submission date)
      // This prevents resubmissions after corrections from overwriting the original date
      if (!currentData?.submittedAt) {
        updateData.submittedAt = serverTimestamp();
        console.log('📝 Setting initial submittedAt (first submission)');
      } else {
        console.log('📝 Preserving existing submittedAt (resubmission after corrections)');
        
        // If corrections were previously requested, this is a resubmission after corrections
        // Track when corrections were completed
        if (currentData?.correctionsRequestedAt && !currentData?.correctionCompletedAt) {
          updateData.correctionCompletedAt = serverTimestamp();
          console.log('📝 Setting correctionCompletedAt (corrections completed and resubmitted)');
        }
      }
      
      // Track FIRST submission time (for accurate time tracking - timer stops here)
      // Only set firstSubmittedAt if it doesn't already exist
      if (status === 'pending_review' && !currentData?.firstSubmittedAt) {
        updateData.firstSubmittedAt = serverTimestamp();
        console.log('⏱️ Captured FIRST submission time - timer stops here for time tracking');
        
        // Calculate template completion time if not already set (in case user submits without saving first)
        if (!currentData?.templateCompletionTime) {
          let sessionStartedAt = currentData?.sessionStartedAt;
          
          // If sessionStartedAt not on product, fetch from session
          if (!sessionStartedAt && currentData?.sessionId) {
            const session = await this.getProductEntrySession(currentData.sessionId);
            sessionStartedAt = session?.createdAt;
          }
          
          if (sessionStartedAt) {
            const now = new Date(); // For template completion calculation only
            const startTime = sessionStartedAt instanceof Date 
              ? sessionStartedAt.getTime() 
              : sessionStartedAt?.toDate?.() 
                ? sessionStartedAt.toDate().getTime()
                : new Date(sessionStartedAt).getTime();
            
            const templateCompletionTime = now.getTime() - startTime;
            updateData.templateCompletionTime = templateCompletionTime;
            updateData.sessionStartedAt = sessionStartedAt instanceof Date 
              ? Timestamp.fromDate(sessionStartedAt)
              : sessionStartedAt;
            
            console.log('⏱️ Template completion time (on submit):', {
              sessionStartedAt,
              firstSubmittedAt: 'serverTimestamp()',
              templateCompletionTime: `${Math.round(templateCompletionTime / 1000)}s (${templateCompletionTime}ms)`
            });
          }
        }
        
        // Also sync audit data from session on first submission
        if (currentData?.sessionId) {
          const session = await this.getProductEntrySession(currentData.sessionId);
          if (session?.audit) {
            updateData.audit = {
              totalTimeSpent: session.audit.totalTimeSpent,
              lastActivityAt: session.audit.lastActivityAt,
              activityCount: session.audit.activityCount
            };
            console.log('⏱️ Synced audit data on first submission:', updateData.audit.totalTimeSpent, 'ms');
          }
        }
      }
      
      // If submitting for final_review (after corrections), record that timestamp
      if (status === 'final_review') {
        updateData.finalReviewSubmittedAt = serverTimestamp();
        updateData.correctionSubmittedAt = serverTimestamp();
        console.log('⏱️ Captured correction submission time for tracking');
        
        // Also update audit data from session if form was opened again for corrections
        if (currentData?.sessionId) {
          const session = await this.getProductEntrySession(currentData.sessionId);
          if (session?.audit && session.audit.totalTimeSpent > (currentData.audit?.totalTimeSpent || 0)) {
            updateData.audit = {
              totalTimeSpent: session.audit.totalTimeSpent,
              lastActivityAt: session.audit.lastActivityAt,
              activityCount: session.audit.activityCount
            };
            console.log('⏱️ Updated audit data on final review submission:', updateData.audit.totalTimeSpent, 'ms');
          }
        }
      }
      
      await updateDoc(productRef, updateData);
      
      // Automatically sync inventory database workflow status
      try {
        // Try multiple ways to get the SKU
        let sku = currentData?.sku;
        if (!sku) {
          sku = currentData?.productData?.variants?.[0]?.sku;
        }
        // If still no SKU, try to get from the session
        if (!sku && currentData?.sessionId) {
          const session = await this.getProductEntrySession(currentData.sessionId);
          sku = session?.sku;
        }
        
        if (sku) {
          console.log(`[ProductEntry] Syncing inventory for SKU: ${sku}, status: ${status}`);
          await this.syncInventoryWorkflowStatus(sku, status);
          // Re-sync product fields (title, price, notes) on status change so inventory row stays populated
          if (currentData?.productData) {
            await this.syncInventoryFromProductData(currentData.productData);
          }
        } else {
          console.warn('[ProductEntry] No SKU found to sync inventory status');
        }
      } catch (syncError) {
        console.error('Error syncing inventory status:', syncError);
        // Don't throw - status update succeeded, sync is secondary
      }
    } catch (error) {
      console.error('Error updating product status:', error);
      throw error;
    }
  }

  // Find inventory database row by SKU (tries multiple field/format variations)
  private async findInventoryItemBySku(sku: string): Promise<{ id: string; data: any } | null> {
    const inventoryRef = collection(db, 'inventoryDatabase');
    const normalizedSku = sku.trim().toUpperCase();
    const seen = new Map<string, any>();

    const collect = (snap: { docs: Array<{ id: string; data: () => Record<string, any> }> }) => {
      snap.docs.forEach(d => {
        if (!seen.has(d.id)) seen.set(d.id, d.data());
      });
    };

    collect(await getDocs(query(inventoryRef, where('sku', '==', sku))));
    collect(await getDocs(query(inventoryRef, where('sku', '==', normalizedSku))));
    collect(await getDocs(query(inventoryRef, where('ourSku', '==', sku))));
    collect(await getDocs(query(inventoryRef, where('ourSku', '==', normalizedSku))));

    if (seen.size === 0) return null;

    // Prefer the row that already has synced product data (avoids updating empty duplicate rows)
    let bestId = '';
    let bestData: any = null;
    let bestScore = -1;

    seen.forEach((data, id) => {
      let score = 0;
      if (data.title?.trim()) score += 10;
      if (data.notes?.trim()) score += 5;
      if (data.price > 0) score += 3;
      if (data.cost > 0) score += 2;
      if (data.retail > 0) score += 2;
      const updatedAt = data.updatedAt?.toDate?.()?.getTime?.() || 0;
      score += updatedAt / 1e15; // tie-break toward most recently updated

      if (score > bestScore) {
        bestScore = score;
        bestId = id;
        bestData = data;
      }
    });

    return { id: bestId, data: bestData };
  }

  private parseMoneyValue(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = parseFloat(String(value).replace(/[$,\s]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }

  private mapMaterialToInventoryMetal(material: string): string | null {
    if (!material?.trim()) return null;
    const lower = material.toLowerCase();
    if (lower.includes('sterling') || lower.includes('silver')) return 'Sterling';
    if (lower.includes('platinum')) return 'Platinum';
    if (lower.includes('palladium')) return 'Palladium';
    if (lower.includes('watch')) return 'Watch';
    const karatMatch = material.match(/(\d+)\s*[kK]/);
    if (karatMatch) return `Gold ${karatMatch[1]}K`;
    if (lower.includes('gold')) return 'Gold 14K';
    return material.trim();
  }

  /** Push product entry fields back into the matching inventoryDatabase row */
  private async syncInventoryFromProductData(productData: any): Promise<void> {
    const sku = productData.variants?.[0]?.sku || productData.sku;
    if (!sku?.trim()) return;

    try {
      const inventoryItem = await this.findInventoryItemBySku(sku);
      if (!inventoryItem) {
        console.warn(`[ProductEntry] No inventory row found to sync for SKU: ${sku}`);
        return;
      }

      const updates: Record<string, any> = {};
      const watchDetails = productData.watchDetails;
      const jewelryDetails = productData.productDetails;
      const isWatch = !!watchDetails || productData.product_type === 'Watches';

      if (productData.title?.trim()) {
        updates.title = productData.title.trim();
      }

      const price = this.parseMoneyValue(productData.variants?.[0]?.price);
      if (price !== null && price >= 0) updates.price = price;

      const cost = this.parseMoneyValue(productData.cost);
      if (cost !== null && cost >= 0) updates.cost = cost;

      const barcode = productData.variants?.[0]?.barcode?.trim();
      if (barcode) updates.barcode = barcode;

      if (isWatch) {
        updates.type = 'WAT';
        updates.metal = 'Watch';

        const retail = this.parseMoneyValue(watchDetails?.approximateRetail);
        if (retail !== null && retail > 0) updates.retail = retail;
      } else if (jewelryDetails) {
        const retail = this.parseMoneyValue(jewelryDetails.approximateRetail);
        if (retail !== null && retail > 0) updates.retail = retail;

        if (jewelryDetails.material) {
          const metal = this.mapMaterialToInventoryMetal(jewelryDetails.material);
          if (metal) updates.metal = metal;
        }

        const weight = this.parseMoneyValue(jewelryDetails.weight);
        if (weight !== null && weight > 0) updates.weightGrams = weight;
      }

      if (Object.keys(updates).length === 0) return;

      await updateInventoryDatabaseItem(inventoryItem.id, updates);
      console.log(`[ProductEntry] ✅ Synced inventory fields for ${sku}:`, Object.keys(updates));
    } catch (error) {
      console.error(`[ProductEntry] Error syncing inventory fields for ${sku}:`, error);
    }
  }

  // Sync inventory database workflow status when product entry status changes
  private async syncInventoryWorkflowStatus(
    sku: string,
    productEntryStatus: string
  ): Promise<void> {
    try {
      // Map product entry status to workflow status
      let workflowStatus: string;
      let tabStatus: string | null = null; // Optionally update tab status too
      
      switch (productEntryStatus) {
        case 'draft':
        case 'in_progress':
          workflowStatus = 'draft';
          tabStatus = 'draft';
          break;
        case 'pending_review':
          workflowStatus = 'sent_for_review';
          tabStatus = 'draft';
          break;
        case 'corrections_needed':
          workflowStatus = 'waiting_corrections';
          tabStatus = 'draft';
          break;
        case 'final_review':
          workflowStatus = 'final_review';
          tabStatus = 'draft';
          break;
        case 'partially_approved':
          workflowStatus = 'partially_approved';
          tabStatus = 'draft';
          break;
        case 'approved':
          workflowStatus = 'approved';
          tabStatus = 'draft'; // Stays in draft until pushed to Shopify
          break;
        case 'scheduled':
          workflowStatus = 'scheduled';
          tabStatus = 'draft'; // Stays in draft until actually pushed
          break;
        case 'listed':
          workflowStatus = 'listed';
          // Tab status will be determined by item type
          tabStatus = null; // Will be set below based on item type
          break;
        default:
          workflowStatus = 'draft';
          tabStatus = 'draft';
      }

      // Find and update the inventory item with robust SKU matching
      const inventoryItem = await this.findInventoryItemBySku(sku);
      
      if (inventoryItem) {
        const itemData = inventoryItem.data;
        
        // For 'listed' status, determine tab based on item type
        if (productEntryStatus === 'listed' && tabStatus === null) {
          const isWatch = itemData.type?.toUpperCase() === 'WAT' || itemData.title?.toLowerCase().includes('watch');
          tabStatus = isWatch ? 'watch' : 'jewelry';
        }
        
        const updateData: any = {
          workflowStatus,
          updatedAt: new Date()
        };
        
        // Only update tab status if it should change
        if (tabStatus) {
          updateData.status = tabStatus;
        }
        
        await updateDoc(doc(db, 'inventoryDatabase', inventoryItem.id), updateData);
        console.log(`[ProductEntry] ✅ Synced inventory ${sku}: workflowStatus → ${workflowStatus}${tabStatus ? `, status → ${tabStatus}` : ''}`);
        
        // Show a subtle toast to confirm sync
        toast.success(`Status synced: ${workflowStatus}`, { 
          duration: 2000,
          icon: '🔄',
          style: { fontSize: '12px' }
        });
      } else {
        console.warn(`[ProductEntry] No inventory item found for SKU: ${sku}`);
      }
    } catch (error) {
      console.error(`[ProductEntry] Error syncing inventory for ${sku}:`, error);
    }
  }

  // Get all jewelry products for review log (one-time fetch)
  async getJewelryProductsForReview(): Promise<JewelryProductEntry[]> {
    try {
      const q = query(
        collection(db, 'jewelryProductEntries'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const products: JewelryProductEntry[] = [];
      
      // Helper to safely convert Firestore timestamp or date string to Date
      const toSafeDate = (val: any): Date | undefined => {
        if (!val) return undefined;
        if (val instanceof Date) return val;
        if (typeof val?.toDate === 'function') return val.toDate();
        if (typeof val === 'string' || typeof val === 'number') {
          const d = new Date(val);
          return isNaN(d.getTime()) ? undefined : d;
        }
        return undefined;
      };

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        products.push({
          id: doc.id,
          ...data,
          createdAt: toSafeDate(data.createdAt) || new Date(),
          updatedAt: toSafeDate(data.updatedAt) || new Date(),
          submittedAt: toSafeDate(data.submittedAt),
          reviewedAt: toSafeDate(data.reviewedAt),
          correctionsRequestedAt: toSafeDate(data.correctionsRequestedAt),
          finalReviewSubmittedAt: toSafeDate(data.finalReviewSubmittedAt),
          pushedToShopifyAt: toSafeDate(data.pushedToShopifyAt),
          shopifyPushStartedAt: toSafeDate(data.shopifyPushStartedAt),
          shopifyPushCompletedAt: toSafeDate(data.shopifyPushCompletedAt),
          shopifyPushErrorAcknowledgedAt: toSafeDate(data.shopifyPushErrorAcknowledgedAt)
        } as JewelryProductEntry);
      });
      
      return products;
    } catch (error) {
      console.error('Error getting jewelry products for review:', error);
      throw error;
    }
  }

  /**
   * Live list of every jewelry entry. The full collection is ~80 MB, so one listener is shared by all
   * screens and kept for a while after the last one closes; going back to Product Entry doesn't
   * download it again. Only changed entries are re-converted on each update.
   */
  subscribeToJewelryProductsForReview(callback: (products: JewelryProductEntry[]) => void): () => void {
    const feed = reviewFeed;
    feed.listeners.add(callback);
    clearTimeout(feed.stopTimer);
    if (feed.products) {
      const current = feed.products;
      queueMicrotask(() => feed.listeners.has(callback) && callback(current));
    }

    if (!feed.unsub) {
      const toSafeDate = (val: any): Date | undefined => {
        if (!val) return undefined;
        if (val instanceof Date) return val;
        if (typeof val?.toDate === 'function') return val.toDate();
        if (typeof val === 'string' || typeof val === 'number') {
          const d = new Date(val);
          return isNaN(d.getTime()) ? undefined : d;
        }
        return undefined;
      };
      const toProduct = (id: string, data: any) => ({
        id,
        ...data,
        createdAt: toSafeDate(data.createdAt) || new Date(),
        updatedAt: toSafeDate(data.updatedAt) || new Date(),
        submittedAt: toSafeDate(data.submittedAt),
        reviewedAt: toSafeDate(data.reviewedAt),
        correctionsRequestedAt: toSafeDate(data.correctionsRequestedAt),
        finalReviewSubmittedAt: toSafeDate(data.finalReviewSubmittedAt),
        pushedToShopifyAt: toSafeDate(data.pushedToShopifyAt),
        shopifyPushStartedAt: toSafeDate(data.shopifyPushStartedAt),
        shopifyPushCompletedAt: toSafeDate(data.shopifyPushCompletedAt),
        shopifyPushErrorAcknowledgedAt: toSafeDate(data.shopifyPushErrorAcknowledgedAt)
      } as JewelryProductEntry);

      const q = query(collection(db, 'jewelryProductEntries'), orderBy('createdAt', 'desc'));
      feed.unsub = onSnapshot(q, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'removed') feed.byId.delete(change.doc.id);
          else feed.byId.set(change.doc.id, toProduct(change.doc.id, change.doc.data()));
        });
        const products = snapshot.docs.map((d) => feed.byId.get(d.id) || toProduct(d.id, d.data()));
        feed.products = products;
        feed.listeners.forEach((l) => l(products));
      }, (error) => {
        console.error('Error subscribing to jewelry products:', error);
        feed.unsub = null;
      });
    }

    return () => {
      feed.listeners.delete(callback);
      if (feed.listeners.size > 0) return;
      clearTimeout(feed.stopTimer);
      feed.stopTimer = setTimeout(() => {
        if (feed.listeners.size > 0) return;
        feed.unsub?.();
        feed.unsub = null;
        feed.products = null;
        feed.byId.clear();
      }, REVIEW_FEED_KEEP_MS);
    };
  }

  // Get a single jewelry product by ID
  async getJewelryProductById(productId: string): Promise<JewelryProductEntry | null> {
    // Helper to safely convert Firestore timestamp or date string to Date
    const toSafeDate = (val: any): Date | undefined => {
      if (!val) return undefined;
      if (val instanceof Date) return val;
      if (typeof val?.toDate === 'function') return val.toDate();
      if (typeof val === 'string' || typeof val === 'number') {
        const d = new Date(val);
        return isNaN(d.getTime()) ? undefined : d;
      }
      return undefined;
    };

    try {
      const productRef = doc(db, 'jewelryProductEntries', productId);
      const productSnap = await getDoc(productRef);
      
      if (!productSnap.exists()) {
        return null;
      }
      
      const data = productSnap.data();
      return {
        id: productSnap.id,
        ...data,
        createdAt: toSafeDate(data.createdAt) || new Date(),
        updatedAt: toSafeDate(data.updatedAt) || new Date(),
        submittedAt: toSafeDate(data.submittedAt),
        reviewedAt: toSafeDate(data.reviewedAt),
        correctionsRequestedAt: toSafeDate(data.correctionsRequestedAt),
        finalReviewSubmittedAt: toSafeDate(data.finalReviewSubmittedAt),
        pushedToShopifyAt: toSafeDate(data.pushedToShopifyAt),
        shopifyPushStartedAt: toSafeDate(data.shopifyPushStartedAt),
        shopifyPushCompletedAt: toSafeDate(data.shopifyPushCompletedAt),
        shopifyPushErrorAcknowledgedAt: toSafeDate(data.shopifyPushErrorAcknowledgedAt)
      } as JewelryProductEntry;
    } catch (error) {
      console.error('Error getting jewelry product:', error);
      throw error;
    }
  }

  /**
   * Lightweight price update for a product entry, used to mirror an inventory
   * database price edit onto the linked product entry. Updates only the price
   * fields (top-level `price` and the first variant's price) and does NOT run
   * the reverse inventory sync, so it won't overwrite other inventory fields
   * or create a sync loop.
   */
  async updateProductEntryPrice(productId: string, price: number): Promise<void> {
    if (!productId || !(price >= 0)) return;
    const ref = doc(db, 'jewelryProductEntries', productId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;

    const data = snap.data() as any;
    const priceStr = price.toFixed(2);
    const productData = data.productData || {};
    const variants = Array.isArray(productData.variants) ? productData.variants : [];
    const updatedVariants =
      variants.length > 0
        ? variants.map((v: any, i: number) => (i === 0 ? { ...v, price: priceStr } : v))
        : [{ price: priceStr }];

    await updateDoc(ref, {
      price: priceStr,
      'productData.variants': updatedVariants,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  }

  // Save product review checklist (supports dual-approval)
  async saveProductReview(
    productId: string,
    reviewData: {
      reviewChecklist: Record<string, { passed: boolean | null; notes: string }>;
      reviewedBy: string;
      reviewedByName: string;
      reviewedAt: Date;
      status: 'approved' | 'rejected' | 'pending_review' | 'corrections_needed';
    }
  ): Promise<void> {
    try {
      const productRef = doc(db, 'jewelryProductEntries', productId);
      
      // Get current product data to check if this is the first corrections request
      const productSnap = await getDoc(productRef);
      const currentProduct = productSnap.data();

      // One reviewer's approval fully approves the entry.
      const effectiveStatus = reviewData.status;

      // Use serverTimestamp() for accurate timestamps
      const updateData: any = {
        reviewChecklist: reviewData.reviewChecklist,
        reviewedBy: reviewData.reviewedBy,
        reviewedByName: reviewData.reviewedByName,
        status: effectiveStatus,
        updatedAt: serverTimestamp()
      };

      // Track individual approvals
      if (reviewData.status === 'approved') {
        const existingApprovals: Array<{ uid: string; name: string; at: any }> = currentProduct?.approvals || [];
        const alreadyApprovedByThisUser = existingApprovals.some((a) => a.uid === reviewData.reviewedBy);
        if (!alreadyApprovedByThisUser) {
          updateData.approvals = [...existingApprovals, {
            uid: reviewData.reviewedBy,
            name: reviewData.reviewedByName,
            at: new Date().toISOString(),
          }];
        }
      }

      // If status goes back to corrections/rejected, reset approvals
      if (reviewData.status === 'corrections_needed' || reviewData.status === 'rejected') {
        updateData.approvals = [];
      }
      
      // Check if this item has already been through corrections (has correctionsRequestedAt)
      const hasBeenThroughCorrections = !!currentProduct?.correctionsRequestedAt;
      
      if (hasBeenThroughCorrections) {
        // This is a FINAL review (after corrections) - set finalReviewSubmittedAt, NOT reviewedAt
        updateData.finalReviewSubmittedAt = serverTimestamp();
        console.log(`📋 Final Review - Setting finalReviewSubmittedAt (preserving original reviewedAt)`);
      } else {
        // This is the INITIAL review - set reviewedAt
        updateData.reviewedAt = serverTimestamp();
        console.log(`📋 Initial Review - Setting reviewedAt`);
      }
      
      // If this is the FIRST time sending to corrections_needed, capture initial review data
      if (reviewData.status === 'corrections_needed' && !currentProduct?.initialReviewChecklist) {
        // Calculate initial accuracy percentage based on Y/N marks (20 total items)
        const checklistItems = Object.values(reviewData.reviewChecklist);
        const totalItems = checklistItems.length;
        const passedItems = checklistItems.filter(item => item.passed === true).length;
        const initialAccuracy = totalItems > 0 ? Math.round((passedItems / totalItems) * 100) : 0;
        
        console.log(`📊 Initial Review Accuracy: ${passedItems}/${totalItems} = ${initialAccuracy}%`);
        
        // Store the initial review data (preserves what failed on first attempt)
        updateData.initialReviewChecklist = reviewData.reviewChecklist;
        updateData.initialReviewAccuracyPercentage = initialAccuracy;
        updateData.correctionsRequestedAt = serverTimestamp();
      }
      
      await updateDoc(productRef, updateData);
      
      // Automatically sync inventory database workflow status
      try {
        // Try multiple ways to get the SKU
        let sku = currentProduct?.sku;
        if (!sku) {
          sku = currentProduct?.productData?.variants?.[0]?.sku;
        }
        // If still no SKU, try to get from the session
        if (!sku && currentProduct?.sessionId) {
          const session = await this.getProductEntrySession(currentProduct.sessionId);
          sku = session?.sku;
        }
        
        if (sku) {
          console.log(`[ProductEntry] Syncing inventory for SKU: ${sku}, review status: ${reviewData.status}`);
          await this.syncInventoryWorkflowStatus(sku, reviewData.status);
        } else {
          console.warn('[ProductEntry] No SKU found to sync inventory status after review');
        }
      } catch (syncError) {
        console.error('Error syncing inventory status:', syncError);
        // Don't throw - review save succeeded, sync is secondary
      }
    } catch (error) {
      console.error('Error saving product review:', error);
      throw error;
    }
  }

  // Start async push to Shopify (non-blocking)
  // `pusher` records WHO initiated the push so per-employee "Published to CCO"
  // reporting can attribute the listing. Optional for backwards compatibility.
  async startAsyncPushToShopify(
    productId: string,
    pusher?: { uid?: string; name?: string }
  ): Promise<void> {
    try {
      // Set status to "pushing" immediately
      const productRef = doc(db, 'jewelryProductEntries', productId);
      await updateDoc(productRef, {
        shopifyPushStatus: 'pushing',
        shopifyPushError: null,
        shopifyPushStartedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Start the push in the background (don't await)
      this.pushProductToShopifyBackground(productId, pusher).catch(error => {
        console.error('Background push error:', error);
      });
    } catch (error) {
      console.error('Error starting async push:', error);
      throw error;
    }
  }

  // Background push process (handles success/error updates)
  private async pushProductToShopifyBackground(
    productId: string,
    pusher?: { uid?: string; name?: string }
  ): Promise<void> {
    const productRef = doc(db, 'jewelryProductEntries', productId);
    
    try {
      // Get product data to get the SKU for inventory sync
      const productSnap = await getDoc(productRef);
      const productData = productSnap.data();
      const sku = productData?.sku || productData?.productData?.variants?.[0]?.sku;
      
      // Call the actual push function and get the final status
      const finalStatus = await this.pushProductToShopify(productId, pusher);
      
      // Update status to the returned final status (success or partial_success)
      await updateDoc(productRef, {
        shopifyPushStatus: finalStatus,
        shopifyPushError: null,
        shopifyPushCompletedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      console.log(`✅ Background Shopify push completed with status: ${finalStatus}`);
      
      // Sync inventory to 'listed' status on successful push
      if (sku && (finalStatus === 'success' || finalStatus === 'partial_success')) {
        try {
          await this.syncInventoryWorkflowStatus(sku, 'listed');
          console.log(`✅ Inventory ${sku} synced to 'listed' status`);
        } catch (syncError) {
          console.error('Error syncing inventory to listed:', syncError);
        }
      }
    } catch (error: any) {
      console.error('❌ Background Shopify push failed:', error);
      
      // Update status to error with error message and completion time
      try {
        await updateDoc(productRef, {
          shopifyPushStatus: 'error',
          shopifyPushError: error.message || 'Unknown error occurred',
          shopifyPushCompletedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        console.log('✅ Error status saved to Firestore');
      } catch (updateError) {
        console.error('❌ Failed to update error status in Firestore:', updateError);
      }
    }
  }

  // NOTE: This method is no longer used for jewelry products with predefined metafields
  // Jewelry metafields are now manually filled in the form
  // Keeping this method for backwards compatibility with older products
  private mapProductDetailsToMetafields(
    existingMetafields: any[], 
    productDetails: any,
    addLog?: (level: 'info' | 'warning' | 'error' | 'success', category: string, message: string, details?: any) => void
  ): any[] {
    const log = addLog || (() => {}); // Use addLog if provided, otherwise no-op
    
    log('info', 'metafields', 'Starting metafield mapping from productDetails (namespace: fin)', {
      existingMetafieldsCount: existingMetafields?.length || 0,
      productDetailsKeys: Object.keys(productDetails || {})
    });
    
    const updatedMetafields = [...existingMetafields];
    const mappingLog: Array<{field: string, value: any, mapped: boolean, reason?: string}> = [];
    
    // Helper to update or add a metafield
    const setMetafield = (namespace: string, key: string, value: any, type: string) => {
      const fieldName = `${namespace}.${key}`;
      
      // Check if value is empty
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        mappingLog.push({ field: fieldName, value, mapped: false, reason: 'Empty value' });
        return;
      }
      
      const index = updatedMetafields.findIndex(m => m.namespace === namespace && m.key === key);
      const formattedValue = typeof value === 'boolean' ? String(value) : String(value);
      
      if (index >= 0) {
        updatedMetafields[index].value = formattedValue;
        mappingLog.push({ field: fieldName, value: formattedValue, mapped: true, reason: 'Updated existing' });
      } else {
        updatedMetafields.push({ namespace, key, type, value: formattedValue });
        mappingLog.push({ field: fieldName, value: formattedValue, mapped: true, reason: 'Added new' });
      }
    };
    
    // Helper to clean and parse numeric values (removes units like 'g', 'oz', etc.)
    const parseNumericValue = (value: any): string | null => {
      if (!value) return null;
      const strValue = String(value).trim();
      // Remove common units and non-numeric characters except decimal point and minus
      const cleaned = strValue.replace(/[^\d.-]/g, '');
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? null : String(parsed);
    };
    
    // Map common jewelry attributes
    setMetafield('fin', 'material', productDetails.material, 'single_line_text_field');
    setMetafield('fin', 'purity', productDetails.purity, 'single_line_text_field');
    
    // Parse weight to ensure it's a valid number (remove 'g', 'oz', etc.)
    const cleanWeight = parseNumericValue(productDetails.weight);
    if (cleanWeight) {
      setMetafield('fin', 'weight', cleanWeight, 'number_decimal');
    }
    
    setMetafield('fin', 'condition', productDetails.overallCondition, 'single_line_text_field');
    setMetafield('fin', 'brand', productDetails.brand, 'single_line_text_field');
    setMetafield('fin', 'has_box', productDetails.box, 'boolean');
    setMetafield('fin', 'has_papers', productDetails.papers, 'boolean');
    setMetafield('fin', 'has_service_papers', productDetails.servicePapers, 'boolean');
    setMetafield('fin', 'has_service_box', productDetails.serviceBox, 'boolean');
    setMetafield('fin', 'inscriptions', productDetails.inscriptionsOrEngravings, 'single_line_text_field');
    setMetafield('fin', 'damages', productDetails.damagesOrImperfections, 'single_line_text_field');
    
    // Map size/dimensions
    if (productDetails.size) {
      setMetafield('fin', 'ring_size', productDetails.size, 'single_line_text_field');
    }
    if (productDetails.length) {
      setMetafield('fin', 'length', productDetails.length, 'single_line_text_field');
    }
    if (productDetails.width) {
      setMetafield('fin', 'width', productDetails.width, 'single_line_text_field');
    }
    
    setMetafield('fin', 'resizable', productDetails.resizable, 'boolean');
    setMetafield('fin', 'clasp_type', productDetails.clasp, 'single_line_text_field');
    setMetafield('fin', 'stamp', productDetails.stamp, 'single_line_text_field');
    
    // Map diamond information if present
    if (productDetails.diamonds && productDetails.diamonds.length > 0) {
      const diamond = productDetails.diamonds[0]; // Use first diamond
      
      // Parse numeric fields properly
      const diamondCarat = parseNumericValue(diamond.totalCaratWeight);
      if (diamondCarat) {
        setMetafield('fin', 'diamond_carat', diamondCarat, 'number_decimal');
      }
      
      const diamondCount = parseNumericValue(diamond.totalStoneCount);
      if (diamondCount) {
        setMetafield('fin', 'diamond_count', Math.floor(parseFloat(diamondCount)).toString(), 'number_integer');
      }
      
      setMetafield('fin', 'diamond_cut', diamond.cut, 'single_line_text_field');
      setMetafield('fin', 'diamond_color', diamond.color, 'single_line_text_field');
      setMetafield('fin', 'diamond_clarity', diamond.clarity, 'single_line_text_field');
      setMetafield('fin', 'diamond_certification', diamond.certification, 'single_line_text_field');
    }
    
    // Map gemstone information if present
    if (productDetails.gemstones && productDetails.gemstones.length > 0) {
      const gemstone = productDetails.gemstones[0]; // Use first gemstone
      setMetafield('fin', 'gemstone_type', gemstone.type, 'single_line_text_field');
      
      // Parse numeric fields properly
      const gemstoneCarat = parseNumericValue(gemstone.totalCaratWeight);
      if (gemstoneCarat) {
        setMetafield('fin', 'gemstone_carat', gemstoneCarat, 'number_decimal');
      }
      
      const gemstoneCount = parseNumericValue(gemstone.totalStoneCount);
      if (gemstoneCount) {
        setMetafield('fin', 'gemstone_count', Math.floor(parseFloat(gemstoneCount)).toString(), 'number_integer');
      }
      
      setMetafield('fin', 'gemstone_cut', gemstone.cut, 'single_line_text_field');
      setMetafield('fin', 'gemstone_color', gemstone.color, 'single_line_text_field');
    }
    
    // Log summary
    const mappedCount = mappingLog.filter(l => l.mapped).length;
    const skippedCount = mappingLog.filter(l => !l.mapped).length;
    const mappedFields = mappingLog.filter(l => l.mapped);
    const skippedFields = mappingLog.filter(l => !l.mapped);
    
    if (mappedCount > 0) {
      log('success', 'metafields', `Successfully mapped ${mappedCount} metafields from product details`, {
        mappedCount,
        skippedCount,
        totalMetafields: updatedMetafields.length,
        mappedFields: mappedFields.map(l => ({ field: l.field, value: l.value })),
        skippedFields: skippedFields.map(l => ({ field: l.field, reason: l.reason }))
      });
    } else {
      log('warning', 'metafields', 'No metafields were mapped from product details', {
        skippedCount,
        skippedFields: skippedFields.map(l => ({ field: l.field, reason: l.reason })),
        productDetailsAvailable: !!productDetails
      });
    }
    
    return updatedMetafields;
  }

  /** Entry-form product type → Shopify product_type; category derived from same field */
  private resolveShopifyProductType(productData: any): string {
    return normalizeProductTypeForShopify(productData?.product_type);
  }

  // Returns the Shopify taxonomy category GID needed for tax + sales channel publishing
  private resolveCategoryGid(productData: any): string | undefined {
    if (productData?.watchDetails || normalizeProductTypeForShopify(productData?.product_type) === 'Watches') {
      return JEWELRY_CATEGORIES['watches'].gid;
    }

    const entryProductType = this.resolveShopifyProductType(productData);
    if (entryProductType) {
      const fromEntryType = suggestCategoryByProductType(entryProductType);
      if (fromEntryType?.gid) return fromEntryType.gid;
    }

    if (productData?.category_gid) {
      return normalizeCategoryGid(productData.category_gid);
    }

    return undefined;
  }

  // Push product to Shopify (actual implementation)
  // Returns the final status: 'success' or 'partial_success'
  async pushProductToShopify(
    productId: string,
    pusher?: { uid?: string; name?: string }
  ): Promise<'success' | 'partial_success'> {
    // Initialize log collector
    const pushLogs: Array<{
      timestamp: Date;
      level: 'info' | 'warning' | 'error' | 'success';
      category: 'product' | 'images' | 'metafields' | 'sales_channels' | 'inventory';
      message: string;
      details?: any;
    }> = [];

    const addLog = (level: 'info' | 'warning' | 'error' | 'success', category: string, message: string, details?: any) => {
      pushLogs.push({
        timestamp: new Date(),
        level,
        category: category as any,
        message,
        details
      });
      // Still log to console for debugging
      const emoji = level === 'success' ? '✅' : level === 'error' ? '❌' : level === 'warning' ? '⚠️' : 'ℹ️';
      console.log(`${emoji} [${category}] ${message}`, details || '');
    };

    try {
      const product = await this.getJewelryProductById(productId);
      
      addLog('info', 'product', `Starting Shopify push for product: ${productId}`);
      
      if (!product) {
        throw new Error('Product not found');
      }

      addLog('info', 'product', `Product loaded: ${product.title} (SKU: ${product.productData?.variants?.[0]?.sku})`);
      
      // Check if product has been reviewed (partially_approved = 1 of 2 approvals, still pushable)
      if (product.status !== 'approved' && product.status !== 'partially_approved' && product.status !== 'pending_review' && product.status !== 'final_review') {
        addLog('error', 'product', `Product status is "${product.status}" - must be reviewed before pushing`);
        throw new Error('Product must be reviewed before pushing to Shopify');
      }

      // Metafields are now manually filled in the product entry form
      // No need to auto-map from productDetails - use them directly
      console.log('📝 METAFIELDS - Using manually filled metafields');
      console.log('   Total metafields:', product.productData.metafields?.length || 0);
      const filledCount = (product.productData.metafields || []).filter((m: any) => m.value && m.value !== '').length;
      console.log('   Filled metafields:', filledCount);
      
      addLog('info', 'metafields', `Using ${filledCount} manually filled metafields for Shopify push`, {
        totalMetafields: product.productData.metafields?.length || 0,
        filledMetafields: filledCount,
        metafieldKeys: (product.productData.metafields || [])
          .filter((m: any) => m.value && m.value !== '')
          .map((m: any) => `${m.namespace}.${m.key}`)
      });

      // Import shopifyService dynamically to avoid circular dependencies
      const { getShopifyService } = await import('./shopifyService');
      const shopifyService = getShopifyService();

      // Calculate total inventory from all locations
      const totalInventory = product.productData.inventoryQuantities?.reduce(
        (sum: number, loc: any) => sum + (parseInt(loc.quantity) || 0), 
        0
      ) || 0;

      // Helper function to format price to exactly 2 decimal places WITHOUT floating-point errors
      const formatPrice = (price: any): string => {
        if (!price || price === '' || price === '0' || price === 0) return '0.00';
        
        // Keep as string to avoid floating-point conversion errors
        let priceStr = String(price).trim();
        
        // Remove any currency symbols or commas
        priceStr = priceStr.replace(/[$,]/g, '');
        
        // Validate it's a valid number string
        if (!/^\d+\.?\d*$/.test(priceStr)) {
          console.warn('Invalid price format:', price);
          return '0.00';
        }
        
        // Split into dollars and cents
        const parts = priceStr.split('.');
        const dollars = parts[0];
        let cents = parts[1] || '00';
        
        // Ensure exactly 2 decimal places
        if (cents.length === 0) {
          cents = '00';
        } else if (cents.length === 1) {
          cents = cents + '0';
        } else if (cents.length > 2) {
          // Truncate to 2 decimals (don't round to avoid further errors)
          cents = cents.substring(0, 2);
        }
        
        const formatted = `${dollars}.${cents}`;
        console.log(`💰 Formatted price: "${price}" → "${formatted}"`);
        return formatted;
      };

      const shopifyProductType = this.resolveShopifyProductType(product.productData);

      // Jewelry products push as ACTIVE; watches stay DRAFT.
      const isWatch = !!product.productData.watchDetails || shopifyProductType === 'Watches';
      const shopifyStatus: 'active' | 'draft' = isWatch ? 'draft' : 'active';

      // Prepare product data for Shopify REST API (clean up any fields that shouldn't be sent)
      // NOTE: category_gid is NOT sent here - it's set via GraphQL after product creation
      const shopifyProductData = {
        title: product.productData.title,
        body_html: product.productData.body_html,
        vendor: product.productData.vendor,
        product_type: shopifyProductType,
        template_suffix: product.productData.template_suffix || 'default', // Theme template based on price
        tags: product.productData.tags || [],
        status: shopifyStatus,
        variants: product.productData.variants?.map((variant: any) => ({
          price: formatPrice(variant.price),
          compare_at_price: variant.compare_at_price ? formatPrice(variant.compare_at_price) : null,
          sku: variant.sku,
          barcode: variant.barcode || null,
          inventory_quantity: totalInventory, // Use total from all locations
          inventory_management: 'shopify',
          weight: variant.weight || null,
          weight_unit: variant.weight_unit || 'g',
          requires_shipping: variant.requires_shipping !== false,
          taxable: variant.taxable !== false
        })) || [],
        // Don't include metafields in initial product creation - they have strict validation
        // and need to be added separately after product is created
        // metafields: []
      };

      addLog('info', 'product', 'Prepared product data for Shopify', {
        title: shopifyProductData.title,
        vendor: shopifyProductData.vendor,
        product_type: shopifyProductData.product_type,
        template_suffix: shopifyProductData.template_suffix,
        price: shopifyProductData.variants?.[0]?.price,
        sku: shopifyProductData.variants?.[0]?.sku,
        inventory: totalInventory
      });

      // Push the product data to Shopify
      addLog('info', 'product', 'Sending product creation request to Shopify...');
      
      // Log category info
      if (product.productData.category_gid) {
        addLog('info', 'product', `Category will be set via GraphQL: ${product.productData.category_gid}`);
      }
      
      let response;
      const categoryGid = this.resolveCategoryGid(product.productData);
      if (categoryGid) {
        addLog('info', 'product', `Using Shopify category for tax & sales channels: ${categoryGid}`);
      } else {
        addLog('warning', 'product', 'No category GID resolved — sales channel publishing may be incomplete');
      }

      try {
        // Pass category_gid so createProduct sets taxonomy before publishing to channels
        response = await shopifyService.createProduct(shopifyProductData, categoryGid);
        addLog('success', 'product', 'Product creation request completed', { hasResponse: !!response });
      } catch (createError: any) {
        addLog('error', 'product', `Product creation failed: ${createError.message}`, {
          errorName: createError.name,
          errorMessage: createError.message,
          productData: {
            title: shopifyProductData.title,
            price: shopifyProductData.variants?.[0]?.price,
            sku: shopifyProductData.variants?.[0]?.sku
          }
        });
        throw createError;
      }
      const shopifyProductId = response?.product?.id?.toString() || '';
      const variantId = response?.product?.variants?.[0]?.id?.toString() || '';

      if (!shopifyProductId) {
        addLog('error', 'product', 'Failed to get product ID from Shopify response');
        throw new Error('Failed to get product ID from Shopify response');
      }

      addLog('success', 'product', `Product created in Shopify with ID: ${shopifyProductId}`);

      // Ensure product category is set (required for correct tax & sales channel availability)
      const resolvedCategoryGid = categoryGid || this.resolveCategoryGid(product.productData);
      if (resolvedCategoryGid) {
        try {
          addLog('info', 'product', `Setting product category: ${resolvedCategoryGid}`);
          const categoryUpdated = await shopifyService.updateProductCategory(shopifyProductId, resolvedCategoryGid);
          if (categoryUpdated) {
            addLog('success', 'product', 'Product category set successfully');
          } else {
            addLog('warning', 'product', 'Failed to set product category');
          }
        } catch (error: any) {
          addLog('warning', 'product', `Error setting product category: ${error.message}`);
        }
      } else if (shopifyProductType) {
        try {
          const suggested = suggestCategoryByProductType(shopifyProductType);
          const categoryId = suggested?.gid || null;
          if (categoryId) {
            addLog('info', 'product', `Using mapped taxonomy category for "${shopifyProductType}": ${categoryId}`);
            const categoryUpdated = await shopifyService.updateProductCategory(shopifyProductId, categoryId);
            if (categoryUpdated) {
              addLog('success', 'product', `Product category set for "${shopifyProductType}"`);
            }
          } else {
            addLog('warning', 'product', `No mapped category for product type "${shopifyProductType}"`);
          }
        } catch (error: any) {
          addLog('warning', 'product', `Error setting product category: ${error.message}`);
        }
      } else {
        addLog('info', 'product', 'No product category available - skipping category assignment');
      }

      // Add metafields after product creation using GraphQL metafieldsSet
      // NOTE: Using GraphQL batch API instead of REST for better reliability
      // All metafields use namespace "fin" and must have definitions created in Shopify Admin
      // (Definitions are auto-created on first run via ensureMetafieldDefinitions)
      addLog('info', 'metafields', 'Checking metafields array for Shopify push', {
        hasMetafieldsArray: !!product.productData.metafields,
        metafieldsArrayLength: product.productData.metafields?.length || 0
      });
      
      if (product.productData.metafields && product.productData.metafields.length > 0) {
        console.log('🏷️  METAFIELDS DEBUG - Starting metafield processing');
        console.log('   Total metafields:', product.productData.metafields.length);
        console.log('   Shopify Product ID:', shopifyProductId);
        console.log('   Raw metafields from product data:', JSON.stringify(product.productData.metafields, null, 2));
        
        // Show which metafields have values
        const filledMetafields = product.productData.metafields.filter((m: any) => 
          m.value !== '' && m.value !== null && m.value !== undefined
        );
        console.log('   Metafields with values:', filledMetafields.length);
        filledMetafields.forEach((m: any) => {
          console.log(`      ✓ ${m.namespace}.${m.key} [${m.type}] = "${m.value}"`);
        });
        
        addLog('info', 'metafields', `Processing ${product.productData.metafields.length} metafields for Shopify`, {
          shopifyProductId,
          totalMetafields: product.productData.metafields.length,
          metafieldList: product.productData.metafields.map((m: any) => ({
            field: `${m.namespace}.${m.key}`,
            type: m.type,
            hasValue: !!m.value,
            valuePreview: m.value ? String(m.value).substring(0, 50) : 'empty'
          }))
        });
        
        try {
          console.log('📦 METAFIELDS - Calling shopifyService.setProductMetafields');
          
          // Ensure metafield definitions exist first (including any auto-created
          // custom keys from eBay required specifics, so they show as defined in Shopify)
          try {
            console.log('🔧 Ensuring metafield definitions exist...');
            const extraKeys = (product.productData.metafields || [])
              .filter((m: any) => m && m.namespace && m.key)
              .map((m: any) => ({ namespace: m.namespace, key: m.key, type: m.type }));
            await shopifyService.ensureMetafieldDefinitions(extraKeys);
            console.log('✅ Metafield definitions verified');
          } catch (defError: any) {
            console.warn('⚠️  Could not verify metafield definitions:', defError.message);
            // Continue anyway - definitions might already exist
          }
          
          // Call setProductMetafields - it handles all validation, formatting, and batching
          await shopifyService.setProductMetafields(shopifyProductId, product.productData.metafields);
          
          // Count how many actually had values
          const filledCount = product.productData.metafields.filter((m: any) => 
            m.value && m.value !== '' && m.value !== null && m.value !== undefined
          ).length;
          
          if (filledCount > 0) {
            addLog('success', 'metafields', `Successfully pushed ${filledCount} metafield${filledCount > 1 ? 's' : ''} to Shopify`, { 
              totalSent: filledCount,
              metafieldKeys: product.productData.metafields
                .filter((m: any) => m.value && m.value !== '')
                .map((m: any) => `${m.namespace}.${m.key}`)
            });
          } else {
            addLog('info', 'metafields', 'No metafields with values to push to Shopify');
          }
        } catch (error: any) {
          addLog('error', 'metafields', `Error in metafield processing: ${error.message}`, {
            error: error.stack
          });
          // Don't fail the whole operation
        }
      } else {
        addLog('warning', 'metafields', 'No metafields array found or array is empty', {
          hasArray: !!product.productData.metafields,
          arrayLength: product.productData.metafields?.length || 0
        });
      }

      // Upload images to Shopify (if any)
      console.log('🔍 Checking for product images...');
      console.log('   imageUrls in productData:', product.productData.imageUrls);
      
      if (product.productData.imageUrls && product.productData.imageUrls.length > 0) {
        let toastId: string | undefined; // Declare outside try block so it's accessible in catch
        try {
          const imageCount = product.productData.imageUrls.length;
          console.log(`📸 Found ${imageCount} image(s) to upload to Shopify`);
          console.log('   Image URLs:', product.productData.imageUrls);
          
          // Show loading toast
          toastId = toast.loading(`Uploading ${imageCount} image${imageCount > 1 ? 's' : ''} to Shopify...`);
          
          let successCount = 0;
          let failCount = 0;
          
          for (let i = 0; i < product.productData.imageUrls.length; i++) {
            const imageUrl = product.productData.imageUrls[i];
            console.log(`\n📤 Uploading image ${i + 1}/${imageCount}`);
            
            try {
              // Update progress toast
              toast.loading(`Uploading image ${i + 1} of ${imageCount} to Shopify...`, { id: toastId });
              
              await shopifyService.addProductImage(shopifyProductId, imageUrl, i);
              console.log(`   ✅ Image ${i + 1} uploaded successfully`);
              successCount++;
            } catch (imageError: any) {
              console.error(`   ❌ Failed to upload image ${i + 1}:`, imageError.message);
              failCount++;
              // Continue with other images
            }
          }
          
          console.log(`\n📊 Image Upload Summary:`);
          console.log(`   ✅ Success: ${successCount}`);
          console.log(`   ❌ Failed: ${failCount}`);
          console.log(`   📝 Total: ${imageCount}`);
          
          // Dismiss the loading toast
          toast.dismiss(toastId);
          
          // Show final result toast with auto-dismiss
          if (failCount === 0 && successCount > 0) {
            addLog('success', 'images', `All ${successCount} image${successCount > 1 ? 's' : ''} uploaded successfully`, { successCount, imageCount });
            toast.success(`✅ All ${successCount} image${successCount > 1 ? 's' : ''} uploaded to Shopify!`, { duration: 3000 });
          } else if (successCount > 0) {
            addLog('warning', 'images', `${successCount} of ${imageCount} images uploaded (${failCount} failed)`, { successCount, failCount, imageCount });
            toast.success(`⚠️ ${successCount} of ${imageCount} images uploaded (${failCount} failed)`, { duration: 4000 });
          } else if (failCount > 0) {
            addLog('error', 'images', `Failed to upload all ${imageCount} images`, { failCount, imageCount });
            toast.error(`❌ Failed to upload images`, { duration: 3000 });
          }
        } catch (error: any) {
          // Dismiss the loading toast if it exists
          if (toastId) {
            toast.dismiss(toastId);
          }
          addLog('error', 'images', `Error in image upload process: ${error.message}`);
          toast.error(`Failed to upload images: ${error.message}`, { duration: 3000 });
          // Don't fail the whole operation if image upload fails
        }
      } else {
        addLog('warning', 'images', 'No images found in product data');
      }


      // Publish to all sales channels
      try {
        addLog('info', 'sales_channels', 'Publishing product to all sales channels...');
        await shopifyService.publishProductToAllChannels(shopifyProductId);
        addLog('success', 'sales_channels', 'Product successfully published to sales channels');
        
        // Verify the publications
        await shopifyService.verifyProductPublications(shopifyProductId);
      } catch (error: any) {
        addLog('warning', 'sales_channels', `Some sales channels may have failed: ${error.message}`);
        // Don't fail the whole operation if publishing fails
        // The product is still created and can be manually published
      }

      // Set up inventory at specific locations if provided
      if (response?.product?.variants?.[0]?.inventory_item_id && product.productData.inventoryQuantities && product.productData.inventoryQuantities.length > 0) {
        try {
          const inventoryItemId = response.product.variants[0].inventory_item_id.toString();
          
          for (const invLocation of product.productData.inventoryQuantities) {
            if (invLocation.quantity > 0) {
              try {
                await shopifyService.setInventoryAtLocation(
                  inventoryItemId, 
                  invLocation.locationId, 
                  parseInt(invLocation.quantity) || 0
                );
                console.log(`✅ Set inventory at ${invLocation.locationName}: ${invLocation.quantity} units`);
              } catch (locError) {
                console.warn(`⚠️ Failed to set inventory at ${invLocation.locationName}:`, locError);
              }
            }
          }
        } catch (error) {
          console.error('⚠️ Failed to set inventory at locations:', error);
          // Don't fail the whole operation
        }
      }

      // Determine final status based on logs (only errors, not warnings!)
      const hasWarnings = pushLogs.some(log => log.level === 'warning');
      const finalStatus = hasWarnings ? 'partial_success' : 'success';
      
      addLog('success', 'product', `Product successfully pushed to Shopify: ${shopifyProductId}`);

      // Clean logs for Firestore (remove undefined values)
      const cleanLogsForFirestore = (logs: typeof pushLogs) => {
        return logs.map(log => {
          const cleanLog: any = {
            timestamp: Timestamp.fromDate(log.timestamp),
            level: log.level,
            category: log.category,
            message: log.message
          };
          // Only add details if it exists and has content
          if (log.details && Object.keys(log.details).length > 0) {
            // Remove undefined values from details
            cleanLog.details = Object.fromEntries(
              Object.entries(log.details).filter(([_, v]) => v !== undefined)
            );
          }
          return cleanLog;
        });
      };

      // Save the final status and logs (to be used by the background wrapper)
      // NOTE: Don't update shopifyPushStatus here - let the background wrapper handle that
      const productRef = doc(db, 'jewelryProductEntries', productId);
      // Attribute the push to the initiating user when we know who they are, so
      // per-employee "Published to CCO" reporting can split the team total.
      const pusherFields: Record<string, string> = {};
      if (pusher?.uid) pusherFields.pushedToShopifyByUid = pusher.uid;
      if (pusher?.name) pusherFields.pushedToShopifyByName = pusher.name;
      try {
        await updateDoc(productRef, {
          shopifyProductId,
          status: 'approved',
          pushedToShopifyAt: Timestamp.fromDate(new Date()),
          ...pusherFields,
          shopifyPushLogs: cleanLogsForFirestore(pushLogs),
          shopifyPushError: null,
          updatedAt: Timestamp.fromDate(new Date())
        });
      } catch (firestoreError: any) {
        // If Firestore save fails, log it but don't fail the push
        console.error('Warning: Failed to save push logs to Firestore:', firestoreError);
        addLog('warning', 'product', `Push succeeded but failed to save logs: ${firestoreError.message}`);
        // Try again without logs
        await updateDoc(productRef, {
          shopifyProductId,
          status: 'approved',
          pushedToShopifyAt: Timestamp.fromDate(new Date()),
          ...pusherFields,
          shopifyPushError: null,
          updatedAt: Timestamp.fromDate(new Date())
        });
      }
      
      // Return the final status so the background wrapper can use it
      console.log(`✅ Product successfully pushed to Shopify with status: ${finalStatus}`);
      return finalStatus;
    } catch (error: any) {
      // THIS is an actual error - the Shopify push failed
      addLog('error', 'product', `Failed to push product to Shopify: ${error.message}`);
      
      // Save error logs to Firestore
      const productRef = doc(db, 'jewelryProductEntries', productId);
      
      // Clean logs for Firestore
      const cleanLogsForFirestore = (logs: typeof pushLogs) => {
        return logs.map(log => {
          const cleanLog: any = {
            timestamp: Timestamp.fromDate(log.timestamp),
            level: log.level,
            category: log.category,
            message: log.message
          };
          if (log.details && Object.keys(log.details).length > 0) {
            cleanLog.details = Object.fromEntries(
              Object.entries(log.details).filter(([_, v]) => v !== undefined)
            );
          }
          return cleanLog;
        });
      };

      // Save error logs to Firestore (but don't update status - let background wrapper handle that)
      try {
        await updateDoc(productRef, {
          shopifyPushLogs: cleanLogsForFirestore(pushLogs),
          updatedAt: Timestamp.fromDate(new Date())
        });
      } catch (firestoreError) {
        // If we can't even save the error logs, just log it
        console.error('Failed to save error logs to Firestore:', firestoreError);
      }
      
      console.error('❌ Error pushing product to Shopify:', error);
      throw error; // Let the background wrapper catch this and update the status
    }
  }

  // Delete jewelry product entry
  async deleteJewelryProductEntry(productId: string): Promise<void> {
    try {
      const productRef = doc(db, 'jewelryProductEntries', productId);
      await deleteDoc(productRef);
      console.log('Product successfully deleted:', productId);
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  }

  // Save watches product entry
  async saveWatchesProductEntry(sessionId: string, data: Partial<WatchesProductEntry>): Promise<string> {
    try {
      const productData: Omit<WatchesProductEntry, 'id'> = {
        sessionId,
        category: 'watches',
        name: data.name || '',
        description: data.description || '',
        sku: data.sku || '',
        price: data.price || 0,
        cost: data.cost || 0,
        brand: data.brand || '',
        model: data.model || '',
        movement: data.movement || '',
        caseMaterial: data.caseMaterial || '',
        bandMaterial: data.bandMaterial || '',
        bandColor: data.bandColor || '',
        dialColor: data.dialColor || '',
        caseSize: data.caseSize || '',
        waterResistance: data.waterResistance || '',
        features: data.features || [],
        images: data.images || [],
        videos: data.videos || [],
        status: 'draft',
        notes: data.notes || '',
        tags: data.tags || [],
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: this.userId,
        createdByName: data.createdByName || ''
      };

      const docRef = await addDoc(collection(db, 'watchesProductEntries'), {
        ...productData,
        createdAt: Timestamp.fromDate(productData.createdAt),
        updatedAt: Timestamp.fromDate(productData.updatedAt)
      });

      return docRef.id;
    } catch (error) {
      console.error('Error saving watches product entry:', error);
      throw error;
    }
  }

  // Get all product entry sessions for user
  async getUserProductEntrySessions(): Promise<ProductEntrySession[]> {
    try {
      const q = query(
        collection(db, 'productEntrySessions'),
        where('createdBy', '==', this.userId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          completedAt: data.completedAt?.toDate()
        } as ProductEntrySession;
      });
    } catch (error) {
      console.error('Error getting user product entry sessions:', error);
      throw error;
    }
  }

  // Delete product entry session
  async deleteProductEntrySession(sessionId: string): Promise<void> {
    try {
      const q = query(
        collection(db, 'productEntrySessions'),
        where('sessionId', '==', sessionId)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        await deleteDoc(docRef);
      }
    } catch (error) {
      console.error('Error deleting product entry session:', error);
      throw error;
    }
  }

  // Acknowledge/dismiss Shopify push errors
  async acknowledgeShopifyPushError(productId: string, userId: string, userName: string): Promise<void> {
    try {
      const productRef = doc(db, 'jewelryProductEntries', productId);
      await updateDoc(productRef, {
        shopifyPushErrorAcknowledged: true,
        shopifyPushErrorAcknowledgedBy: userName,
        shopifyPushErrorAcknowledgedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      console.log(`✅ Shopify push error acknowledged for product ${productId} by ${userName}`);
    } catch (error) {
      console.error('Error acknowledging Shopify push error:', error);
      throw error;
    }
  }

  // Reset stuck "pushing" status to idle (for products that got stuck)
  async resetStuckPushStatus(productId: string): Promise<void> {
    try {
      const productRef = doc(db, 'jewelryProductEntries', productId);
      const productSnap = await getDoc(productRef);
      
      if (!productSnap.exists()) {
        throw new Error('Product not found');
      }
      
      const product = productSnap.data();
      
      // Only reset if status is "pushing"
      if (product.shopifyPushStatus === 'pushing') {
        await updateDoc(productRef, {
          shopifyPushStatus: 'idle',
          shopifyPushError: 'Push was stuck and has been reset. Please try again.',
          shopifyPushCompletedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        console.log(`✅ Reset stuck push status for product ${productId}`);
      } else {
        console.log(`⚠️ Product ${productId} status is "${product.shopifyPushStatus}", not stuck`);
      }
    } catch (error) {
      console.error('Error resetting stuck push status:', error);
      throw error;
    }
  }

  // Update button permissions for a product
  async updateProductButtonPermissions(
    productId: string, 
    permissions: { printReport: boolean; delete: boolean; repushToShopify: boolean }
  ): Promise<void> {
    try {
      const productRef = doc(db, 'jewelryProductEntries', productId);
      await updateDoc(productRef, {
        buttonPermissions: permissions,
        updatedAt: Timestamp.fromDate(new Date())
      });
      console.log(`✅ Button permissions updated for product ${productId}`, permissions);
    } catch (error) {
      console.error('Error updating button permissions:', error);
      throw error;
    }
  }
}

export const productEntryService = new ProductEntryService();
